import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_VideoToText = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/video-to-text',
			name: 'Video to Text Converter: Get a Transcript from Any MP4',
			description:
				'Transcribe video to text with AI: upload a vlog, webinar, or lesson and get every line of dialogue in writing. Try Lift today!',
			url: 'https://liftapp.ai/video-to-text',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/video-to-text#product' },
				{ '@id': 'https://liftapp.ai/video-to-text#howto' },
				{ '@id': 'https://liftapp.ai/video-to-text#benefits' },
				{ '@id': 'https://liftapp.ai/video-to-text#faq' },
				{ '@id': 'https://liftapp.ai/video-to-text#why-lift' },
				{ '@id': 'https://liftapp.ai/video-to-text#explore-tools' },
				{ '@id': 'https://liftapp.ai/video-to-text#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/video-to-text#product',
			name: 'AI Video Transcript Generator',
			description:
				"Upload a video, and Lift's AI pulls out the speech and turns it into text you can copy, summarize, or download. Try Lift today!",
			url: 'https://liftapp.ai/video-to-text',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/video-to-text#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload an MP4 or other video',
					text: 'Add a file from your device, from short clips to full-length webinars and lessons.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Adjust the language and audio cues',
					text: 'Auto works it out for you, or set it manually and decide whether to tag background sounds.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Turn the footage into text',
					text: 'Tap Generate, and Lift extracts the speech from your clip and writes it out in My Transcriptions.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download or reuse the transcript',
					text: 'Copy the dialogue, tidy it with Clean up, or save it as a TXT or PDF document.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-to-text#benefits',
			name: 'Who can benefit from the Video to Text Converter?',
			description:
				"Lift's Video to Text Converter helps course creators, editors, marketers, social media managers, and HR teams turn recorded video into written text.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Online course creators — Turn recorded lessons into written guides and handouts for students who prefer reading.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video editors — Read the full dialogue of raw footage to find the strongest lines before cutting.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Marketers — Convert webinar and product demo recordings into text for blog posts and email campaigns.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Social media managers — Transcribe vlogs and reels to reuse the best lines in captions and posts.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'HR and training teams — Turn onboarding and compliance videos into searchable text for new employees.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/video-to-text/benefits.webp',
				name: 'Video to Text Converter by Lift',
				description:
					'Hand holding a phone with a list of saved MP4 transcriptions and a new video being converted',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/video-to-text#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Video to Text Converter?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift Video to Text Converter uses AI to extract speech from a video and turn it into a written transcript. It works with uploaded clips, recorded webinars, lessons, and vlogs.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Video to Text Converter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a video, keep the language on Auto or pick it, and tap Generate. When the transcript is ready, copy it, clean it up, summarize it, or download it.',
					},
				},
				{
					'@type': 'Question',
					name: 'Do I need to convert my video to audio first?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'No. Upload an MP4, MOV, or another common video format directly, and Lift pulls the speech from the soundtrack for you.',
					},
				},
				{
					'@type': 'Question',
					name: 'How can I save my video transcript?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Use Copy to send everything to your clipboard, or Download to get a TXT or PDF file. If you also created a summary, export it on its own or together with the transcription.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my videos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your videos securely and uses them only to generate your transcript.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-to-text#why-lift',
			name: 'Why choose Lift to turn video into text',
			description:
				"Lift's Video to Text Converter handles everything from casual clips to full episodes and keeps every transcript saved in one affordable tool on iOS, Android, and Web.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/video-to-text/whyLift.webp',
				name: 'Why use Lift to turn video into text',
				description:
					'Tablet on a café table playing a two-person scene beside its AI video transcription, ready to copy',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-to-text#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/video-to-text#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video to Text',
					item: 'https://liftapp.ai/video-to-text',
				},
			],
		},
	],
};
